import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  MessageCircle, 
  Send, 
  Loader2,
  Headphones,
  CheckCheck,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupportChatPage = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [caseId, setCaseId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChat();
    // Poll for new messages every 10 seconds
    const interval = setInterval(fetchChat, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChat = async () => {
    try {
      const res = await axios.get(`${API}/support-chat/my-chat`);
      setMessages(res.data.messages || []);
      setCaseId(res.data.case_id);
      
      // Mark messages as read
      if (res.data.unread_count > 0) {
        await axios.post(`${API}/support-chat/mark-read`);
      }
    } catch (error) {
      console.error("Error fetching chat:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await axios.post(`${API}/support-chat/send`, {
        content: newMessage.trim(),
        case_id: caseId
      });
      setNewMessage("");
      await fetchChat();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="support-chat-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-t-2xl p-6 text-white"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <Headphones className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Soporte Divídelo</h1>
              <p className="text-emerald-100 text-sm">
                Estamos aquí para ayudarte con tu proceso
              </p>
            </div>
          </div>
        </motion.div>

        {/* Messages Container */}
        <div className="bg-white border-x border-navy-100 h-[500px] overflow-y-auto p-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageCircle className="w-16 h-16 text-navy-200 mb-4" />
              <p className="text-navy-500 mb-2">Aún no hay mensajes</p>
              <p className="text-sm text-navy-400">
                Escribe tu primera pregunta y te responderemos lo antes posible
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <motion.div
                  key={msg.id || index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.sender_role === "client" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.sender_role === "client"
                        ? "bg-emerald-600 text-white rounded-br-md"
                        : "bg-navy-100 text-navy-900 rounded-bl-md"
                    }`}
                  >
                    {msg.sender_role === "admin" && (
                      <p className="text-xs font-semibold text-emerald-700 mb-1">
                        Soporte Divídelo
                      </p>
                    )}
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                    <div className={`flex items-center gap-1 mt-1 text-xs ${
                      msg.sender_role === "client" ? "text-emerald-200" : "text-navy-400"
                    }`}>
                      <Clock className="w-3 h-3" />
                      {new Date(msg.created_at).toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                      {msg.sender_role === "client" && msg.read_by_admin && (
                        <CheckCheck className="w-3 h-3 ml-1" />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="bg-white border border-navy-100 rounded-b-2xl p-4">
          <div className="flex gap-3">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu mensaje..."
              className="resize-none min-h-[60px]"
              rows={2}
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="bg-emerald-600 hover:bg-emerald-700 px-6"
              data-testid="send-message-btn"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-navy-400 mt-2">
            Presiona Enter para enviar • Shift+Enter para nueva línea
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SupportChatPage;
